import React, { useState, useMemo } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Description as PassportIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import axios from 'axios';
import AuthorFormModal from '../components/AuthorFormModal';
import { textMatchesSearch } from '../utils/searchNormalize';
import { useAuth } from '../contexts/AuthContext';
import { hasPermission } from '../utils/permissions';

const PASSPORT_FORMATS = [
  { id: 'txt', label: 'Текст (TXT)' },
  { id: 'docx', label: 'Документ (DOCX)' },
  { id: 'html', label: 'Веб-страница (HTML)' },
  { id: 'json', label: 'Данные (JSON)' },
];

export default function AuthorsList() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editingAuthor, setEditingAuthor] = useState(null);
  const [passportAnchor, setPassportAnchor] = useState(null);
  const [passportAuthor, setPassportAuthor] = useState(null);
  const [passportLoading, setPassportLoading] = useState(false);
  const [authorToDelete, setAuthorToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  const { data: authors = [], isLoading, refetch } = useQuery(
    ['authors'],
    async () => {
      const response = await axios.get('/api/authors', { params: { limit: 10000 } });
      return response.data;
    },
    { staleTime: 5 * 60 * 1000 }
  );

  const filteredAuthors = useMemo(() => {
    if (!search.trim()) return authors;
    return authors.filter((author) =>
      textMatchesSearch(
        search,
        author.full_name,
        author.last_name,
        author.bio
      )
    );
  }, [authors, search]);

  const handleSearchClick = () => {
    setSearch(searchInput);
  };

  const handleAdd = () => {
    setEditingAuthor(null);
    setModalOpen(true);
  };

  const handleEdit = (author) => {
    setEditingAuthor(author);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setEditingAuthor(null);
  };

  const handleSuccess = () => {
    refetch();
    handleCloseModal();
  };

  const handleDeleteOpen = (author) => {
    setDeleteError(null);
    setAuthorToDelete(author);
  };

  const handleDeleteCancel = () => {
    if (deleteLoading) return;
    setAuthorToDelete(null);
    setDeleteError(null);
  };

  const handleDeleteConfirm = async () => {
    if (!authorToDelete) return;
    setDeleteLoading(true);
    setDeleteError(null);
    try {
      await axios.delete(`/api/authors/${authorToDelete.id}`);
      setAuthorToDelete(null);
      queryClient.invalidateQueries('authors');
    } catch (err) {
      setDeleteError(err.response?.data?.error || 'Не удалось удалить автора');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handlePassportOpen = (event, author) => {
    setPassportAnchor(event.currentTarget);
    setPassportAuthor(author);
  };

  const handlePassportClose = () => {
    setPassportAnchor(null);
    setPassportAuthor(null);
  };

  const handlePassportFormat = async (format) => {
    if (!passportAuthor) return;
    setPassportLoading(true);
    try {
      const response = await axios.get(`/api/authors/${passportAuthor.id}/passport`, {
        params: { format },
        responseType: 'blob',
      });
      const disposition = response.headers['content-disposition'];
      let filename = `passport_${passportAuthor.full_name.replace(/[^a-zA-Zа-яА-ЯёЁ0-9 -_]/g, '_')}.${format}`;
      if (disposition) {
        const match = /filename[*]?=['"]?(?:UTF-8'')?([^'";\n]+)/i.exec(disposition);
        if (match) filename = match[1].trim();
      }
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
      if (err.response?.data instanceof Blob) {
        const text = await err.response.data.text();
        try {
          const json = JSON.parse(text);
          alert(json.error || 'Ошибка при формировании паспорта');
        } catch {
          alert('Ошибка при формировании паспорта');
        }
      } else {
        alert(err.response?.data?.error || 'Ошибка при формировании паспорта');
      }
    } finally {
      setPassportLoading(false);
      handlePassportClose();
    }
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
        <h2>Авторы</h2>
        {hasPermission(user, 'edit_artworks') && (
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAdd}
        >
          Добавить автора
        </Button>
        )}
      </Box>

      <Box display="flex" gap={1} alignItems="flex-start" sx={{ mb: 2, maxWidth: 520 }}>
        <TextField
          fullWidth
          label="Поиск"
          variant="outlined"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleSearchClick()}
          placeholder="Поиск… Несколько слов через +: имя+фамилия"
        />
        <Button
          variant="contained"
          startIcon={<SearchIcon />}
          onClick={handleSearchClick}
          sx={{ minWidth: 100, flexShrink: 0 }}
        >
          Поиск
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>ФИО</TableCell>
              <TableCell>Год рождения</TableCell>
              <TableCell>Год смерти</TableCell>
              <TableCell>Произведений</TableCell>
              <TableCell align="right">Действия</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredAuthors.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                  {search ? 'Ничего не найдено' : 'Нет авторов. Добавьте первого.'}
                </TableCell>
              </TableRow>
            ) : (
              filteredAuthors.map((author) => (
                <TableRow key={author.id} hover>
                  <TableCell>{author.full_name || '—'}</TableCell>
                  <TableCell>{author.birth_year ?? '—'}</TableCell>
                  <TableCell>{author.death_year ?? '—'}</TableCell>
                  <TableCell>{author.artworks_count ?? 0}</TableCell>
                  <TableCell align="right">
                    {hasPermission(user, 'edit_artworks') && (
                    <IconButton
                      size="small"
                      onClick={() => handleEdit(author)}
                      title="Редактировать"
                    >
                      <EditIcon />
                    </IconButton>
                    )}
                    <IconButton
                      size="small"
                      onClick={(e) => handlePassportOpen(e, author)}
                      title="Получить паспорт автора"
                      disabled={passportLoading}
                    >
                      <PassportIcon />
                    </IconButton>
                    {hasPermission(user, 'edit_artworks') && (
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleDeleteOpen(author)}
                      title="Удалить"
                    >
                      <DeleteIcon />
                    </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Menu
        anchorEl={passportAnchor}
        open={Boolean(passportAnchor)}
        onClose={handlePassportClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {PASSPORT_FORMATS.map((f) => (
          <MenuItem
            key={f.id}
            onClick={() => handlePassportFormat(f.id)}
          >
            <ListItemIcon>
              <PassportIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{f.label}</ListItemText>
          </MenuItem>
        ))}
      </Menu>

      <AuthorFormModal
        open={modalOpen}
        onClose={handleCloseModal}
        author={editingAuthor}
        onSuccess={handleSuccess}
      />

      <Dialog open={Boolean(authorToDelete)} onClose={handleDeleteCancel}>
        <DialogTitle>Удалить автора?</DialogTitle>
        <DialogContent>
          {deleteError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setDeleteError(null)}>
              {deleteError}
            </Alert>
          )}
          <DialogContentText>
            Удалить автора «{authorToDelete?.full_name}»? Это действие нельзя отменить.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} disabled={deleteLoading}>
            Отмена
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deleteLoading}
          >
            {deleteLoading ? 'Удаление…' : 'Удалить'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
